import {SandboxInstance} from '@blaxel/core';
import {SANDBOX_WORKING_DIR} from '../constants';
import {execDurationMs} from './serverTiming';

export type ScannedImports = {
  packages: string[];
  /** Specifiers the sandbox-side validator dropped — surfaced to telemetry. */
  rejected: string[];
  /**
   * Blaxel process duration (ms) attributed to this scan phase. Null when
   * the exec threw — caller treats that as "can't attribute" and falls back
   * to client-measured timing.
   */
  serverDurationMs: number | null;
};

/**
 * Glob patterns the sandbox-side scanner walks to find files whose bare
 * imports must be installed. Components live at the sandbox root; function
 * files (`*.webflow.function.*`, EXT-168 / Code Functions M1.2) may live in
 * subfolders, so they need a recursive pattern.
 *
 * Duplicated verbatim in `scan-imports.ts` — see the drift test in
 * `isValidNpmPackageName.test.ts` for why, and keep both in sync.
 */
export const SANDBOX_IMPORT_SCAN_GLOBS = [
  '*.webflow.tsx',
  '**/*.webflow.function.{ts,tsx}',
] as const;

/**
 * Coerces an unknown value into a `string[]`. Anything that isn't an array
 * of strings becomes `[]`. Used to defensively parse the sandbox's JSON
 * output, which can be a flat array (legacy shape) or an object with
 * `packages` / `rejected` fields (current shape).
 */
const toStringArray = (value: unknown): string[] =>
  Array.isArray(value)
    ? value.filter((x): x is string => typeof x === 'string')
    : [];

/**
 * Scans the sandbox's component and function files (see
 * {@link SANDBOX_IMPORT_SCAN_GLOBS}) for bare import specifiers
 * using Bun's native Transpiler.scanImports(). Returns unique package names,
 * any specifiers the sandbox-side validator rejected as not-a-package
 * (APAI-3268), and Blaxel-reported wall-clock duration so the caller can
 * attribute scan time to the `deps_install` phase without being skewed by
 * client tab throttling.
 *
 * The scan-imports.ts script is baked into the sandbox image via the
 * Dockerfile, so we just execute it directly without writing it each time.
 * Older image versions returned a flat string array; this function accepts
 * both shapes so a stale sandbox doesn't break the build.
 *
 * TODO(APAI-3419): once this PR has been deployed past the sandbox TTL
 * (~30 min), every live sandbox emits the object shape — drop the legacy
 * flat-array branch below.
 */
export async function scanUserImports(
  sandbox: SandboxInstance
): Promise<ScannedImports> {
  try {
    const result = await sandbox.process.exec({
      command: `bun run ${SANDBOX_WORKING_DIR}/scan-imports.ts`,
      workingDir: SANDBOX_WORKING_DIR,
      waitForCompletion: true,
    });
    const serverDurationMs = execDurationMs(result);
    const parsed: unknown = JSON.parse(result.logs);
    if (Array.isArray(parsed)) {
      // Legacy sandbox image — only the packages list, no rejection info.
      return {packages: toStringArray(parsed), rejected: [], serverDurationMs};
    }
    if (parsed && typeof parsed === 'object') {
      const obj = parsed as {packages?: unknown; rejected?: unknown};
      return {
        packages: toStringArray(obj.packages),
        rejected: toStringArray(obj.rejected),
        serverDurationMs,
      };
    }
    return {packages: [], rejected: [], serverDurationMs};
  } catch {
    return {packages: [], rejected: [], serverDurationMs: null};
  }
}
